import * as React from 'react';
import { CHART_INK, formatPercent, luminance, sequentialColor } from './chart-utils';
import { ChartTooltip, type ChartTooltipState } from './chart-tooltip';

export type CohortRow = {
  key: string;
  label: string;
  size: number;
  /** One value per column (0..100); null = not yet reached / no data. */
  cells: (number | null)[];
};

export type CohortHeatmapProps = {
  rows: CohortRow[];
  columnLabels: string[];
  valueFormat?: (n: number) => string;
  /** Header of the size column, e.g. "Students". */
  sizeLabel?: string;
  /** Header of the row-label column, e.g. "Joined". */
  rowLabel?: string;
  emptyLabel?: string;
  className?: string;
  /** When set, cells hold raw values and the colour is value ÷ max (0..100 otherwise). */
  max?: number;
  /** Hide the size column (e.g. for a weekday × hour heatmap). */
  showSize?: boolean;
};

/** Cohort grid: rows = cohorts, columns = periods since, cell = value on a blue ramp. */
export function CohortHeatmap({
  rows,
  columnLabels,
  valueFormat = formatPercent,
  sizeLabel = 'Size',
  rowLabel = 'Cohort',
  emptyLabel = 'No data',
  className,
  max,
  showSize = true,
}: CohortHeatmapProps) {
  const colorOf = (value: number) => sequentialColor(max ? (value / Math.max(1, max)) * 100 : value);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = React.useState<ChartTooltipState | null>(null);
  const [containerWidth, setContainerWidth] = React.useState(600);

  if (rows.length === 0) {
    return (
      <div className="py-8 text-center text-sm" style={{ color: CHART_INK.muted }}>
        {emptyLabel}
      </div>
    );
  }

  const showTooltip = (
    e: React.MouseEvent<HTMLTableCellElement>,
    row: CohortRow,
    col: number,
    value: number,
  ) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setContainerWidth(rect.width || 600);
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      title: `${row.label} · ${columnLabels[col] ?? col}`,
      rows: [
        ...(showSize ? [{ label: sizeLabel, value: String(row.size) }] : []),
        { label: columnLabels[col] ?? String(col), value: valueFormat(value) },
      ],
    });
  };

  return (
    <div
      ref={containerRef}
      className={['relative w-full overflow-x-auto', className].filter(Boolean).join(' ')}
      data-testid="cohort-heatmap"
    >
      <table className="w-full border-separate" style={{ borderSpacing: 2, fontSize: 12 }}>
        <thead>
          <tr>
            <th className="px-2 py-1 text-left font-medium" style={{ color: CHART_INK.secondary }}>
              {rowLabel}
            </th>
            {showSize ? (
              <th className="px-2 py-1 text-right font-medium" style={{ color: CHART_INK.secondary }}>
                {sizeLabel}
              </th>
            ) : null}
            {columnLabels.map((label) => (
              <th key={label} className="px-1 py-1 text-center font-medium" style={{ color: CHART_INK.secondary }}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="whitespace-nowrap px-2 py-1" style={{ color: CHART_INK.primary }}>
                {row.label}
              </td>
              {showSize ? (
                <td className="px-2 py-1 text-right tabular-nums" style={{ color: CHART_INK.secondary }}>
                  {row.size}
                </td>
              ) : null}
              {columnLabels.map((label, col) => {
                const value = row.cells[col] ?? null;
                if (value === null) {
                  return (
                    <td
                      key={label}
                      data-testid="cohort-cell"
                      data-empty="true"
                      className="h-8 min-w-[2.75rem] rounded-sm"
                      style={{ border: `1px solid ${CHART_INK.grid}` }}
                    />
                  );
                }
                const fill = colorOf(value);
                const ink = luminance(fill) > 0.45 ? CHART_INK.primary : CHART_INK.surface;
                return (
                  <td
                    key={label}
                    data-testid="cohort-cell"
                    data-empty="false"
                    className="h-8 min-w-[2.75rem] rounded-sm text-center tabular-nums"
                    style={{ backgroundColor: fill, color: ink }}
                    onMouseMove={(e) => showTooltip(e, row, col, value)}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    {valueFormat(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <ChartTooltip state={tooltip} containerWidth={containerWidth} />
    </div>
  );
}
